// One slot for an armed pick (docs/claude/tools.md, *An armed pick*): at most one
// "the next click means X" is armed at a time, and however it ends — its own
// click, a cancel, another pick armed over it, a selection it no longer fits —
// it goes the same way: out of the slot FIRST, so whatever its ending calls (a
// prompt refresh, a setTool) finds nothing armed, and then `changed` once.
// DOM-free: what a pick does lives in the pick (ui/armedPick.ts).

/** What the slot needs of a pick. */
export interface Armed {
  /** Let go without its work done: put back what arming it changed. */
  cancel(): void;
  /** Whether it still means something (its room still the one selected, …):
   *  false ends it at the next `settle`. */
  holds?(): boolean;
}

export interface ArmedSlot<P extends Armed> {
  /** Arm a pick; the one armed before it is cancelled first. */
  arm(p: P): void;
  /** The armed pick, or null. */
  current(): P | null;
  /** Let go and put back; true when one was armed. */
  cancel(): boolean;
  /** Its work done (its marquee, the segment it waited for): let go without
   *  putting back. The pick it held, or null. */
  end(): P | null;
  /** The armed pick's next click: the slot is empty while `run` runs, and the
   *  pick is armed again only when `run` says 'keep' (a click that missed) and
   *  nothing else was armed meanwhile. False when nothing was armed. */
  take(run: (p: P) => 'keep' | void): boolean;
  /** Cancel a pick that no longer holds; true when one went. */
  settle(): boolean;
}

/** The slot. `changed(now, before)` runs once after every change. */
export function armedSlot<P extends Armed>(changed: (now: P | null, before: P | null) => void): ArmedSlot<P> {
  let cur: P | null = null;
  const letGo = (putBack: boolean): P | null => {
    const p = cur;
    if (!p) return null;
    cur = null;
    if (putBack) p.cancel();
    changed(cur, p);
    return p;
  };
  return {
    arm(p) {
      const before = cur;
      cur = null;
      before?.cancel();
      cur = p;
      changed(cur, before);
    },
    current: () => cur,
    cancel: () => letGo(true) !== null,
    end: () => letGo(false),
    take(run) {
      const p = cur;
      if (!p) return false;
      cur = null;
      if (run(p) === 'keep' && cur === null) cur = p;
      changed(cur, p);
      return true;
    },
    settle: () => (cur?.holds && !cur.holds() ? letGo(true) !== null : false),
  };
}
